import { createSlice, createSelector } from '@reduxjs/toolkit';
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, initMapFactory } from '../common/reducers/utilities';
import { ensureArray } from '../common/utils/arrayUtilities';

const compareCrewForm = (f1, f2) => f1.id === f2.id;

const crewCustomFormSlice = createSlice({
    name: 'crewCustomForm',
    initialState: {
        defs: [],
        crewForms: {}
    },
    reducers: {
        setDefs: (state, action) => {
            state.defs = action.payload;
        },
        addOrUpdateFormDef: (state, action) => {
            for (const def of ensureArray(action.payload)) {
                const idx = state.defs.findIndex(d => d.id === def.id);
                if (idx > -1) {
                    state.defs.splice(idx, 1, def);
                } else {
                    state.defs.push(def);
                }
            }
        },
        removeFormDef: (state, action) => {
            for (const def of ensureArray(action.payload)) {
                const idx = state.defs.findIndex(d => def.id === d.id);
                if (idx > -1) {
                    state.defs.splice(idx, 1);
                }
            }
        },
        setCrewForms: (state, action) => {
            let forms = action.payload;
            if (!Array.isArray(forms)) {
                forms = [forms];
            }
            forms.forEach(form => {
                if (state.crewForms[form.callsign]) {
                    delete state.crewForms[form.callsign];
                }
            })
            forms.forEach(form => {
                const callsign = form.callsign;
                if (!state.crewForms[callsign]) {
                    state.crewForms[callsign] = []
                }
                state.crewForms[callsign].push(form);
            });
        },
        addOrUpdateForm: (state, action) => {
            for (const form of ensureArray(action.payload)) {
                const callsign = form.callsign;
                if (state.crewForms[callsign]) {
                    let idx = state.crewForms[callsign].findIndex(f => compareCrewForm(form, f));
                    if (idx >= 0) {
                        state.crewForms[callsign].splice(idx, 1, form);
                    } else {
                        state.crewForms[callsign].push(form);
                    }
                } else {
                    state.crewForms[callsign] = [form];
                }
            }
        },
        removeForm: (state, action) => {
            for (const crew of ensureArray(action.payload)) {
                const callsign = crew.callsign;
                if (state.crewForms[callsign]) {
                    let idx = state.crewForms[callsign].findIndex(form => compareCrewForm(form, crew));
                    if (idx >= 0) {
                        state.crewForms[callsign].splice(idx, 1);
                    }
                }
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => crewCustomFormSlice.getInitialState());
    }
});

// #region network operations
const fetchDefs = network => network.get('/api/v1/customFormCrewDefs', crewCustomFormSlice.actions.setDefs);
const fetchCrewForms = callsign => network => network.get('/api/v1/customCrewFormSearch/' + encodeURIComponent(callsign), crewCustomFormSlice.actions.setCrewForms);
export const insertCrewForm = (network, form) => network.post('/api/v1/customCrewForm', form, crewCustomFormSlice.actions.addOrUpdateForm);
export const updateCrewForm = (network, form) => network.put('/api/v1/customCrewForm', form, crewCustomFormSlice.actions.addOrUpdateForm);
export const insertCrewFormDef = (network, def) => network.post('/api/v1/customFormCrewDef', def, crewCustomFormSlice.actions.addOrUpdateFormDef);
export const updateCrewFormDef = (network, def) => network.put('/api/v1/customFormCrewDef', def, crewCustomFormSlice.actions.addOrUpdateFormDef);
export const removeCrewFormDef = (network, def) => network.delete('/api/v1/customFormCrewDef/' + encodeURIComponent(def.id), crewCustomFormSlice.actions.removeFormDef);
// #endregion

// #region selectors
const selectCrewFormDefs = state => state.crewCustomForm.defs;
const selectForms = state => state.crewCustomForm.crewForms;
const selectCrewFormsByCrewID = createSelector([selectForms, (forms, callsign) => callsign], (forms, callsign) => forms[callsign] ?? []);
const selectCrewForms = callsign => state => selectCrewFormsByCrewID(state, callsign);
// #endregion

// #region permissions
export const canCreateCrewFormDef = (credentials, def) => credentials.access.hasFull('administrator');
export const canUpdateCrewFormDef = canCreateCrewFormDef;
export const canRemoveCrewFormDef = canCreateCrewFormDef;
// #endregion

// #region listeners
const listenCrewFormDef = {
    crewFormDefCreated: (def, { dispatch }) => dispatch(crewCustomFormSlice.actions.addOrUpdateFormDef(def)),
    crewFormDefUpdated: (def, { dispatch }) => dispatch(crewCustomFormSlice.actions.addOrUpdateFormDef(def)),
    crewFormDefRemoved: (def, { dispatch }) => dispatch(crewCustomFormSlice.actions.removeFormDef(def))
}
const listenCrewForm = {
    crewFormCreated: (form, { dispatch }) => dispatch(crewCustomFormSlice.actions.addOrUpdateForm(form)),
    crewFormUpdated: (form, { dispatch }) => dispatch(crewCustomFormSlice.actions.addOrUpdateForm(form))
}
// #endregion

export const [useCrewFormDefs] = dataHookFactory(selectCrewFormDefs, { run: fetchDefs, listen: listenCrewFormDef });
export const [useCrewForms] = dataHookFactory(selectCrewForms, initMapFactory(fetchCrewForms, listenCrewForm))

const reducerDetails = { name : crewCustomFormSlice.name, reducer: crewCustomFormSlice.reducer };
export default reducerDetails;